import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';

const SOURCE = 'hanro-gift-box-drawer';

/**
 * Hanro — Gift Box Drawer (ECSR-710).
 *
 * Adds / removes the store's gift box product from the PDP and keeps its
 * "added" state in sync with the cart. Expand/collapse is handled by the
 * parent <accordion-custom>; visual state is driven purely by the
 * `data-in-cart` attribute on the host so a section-rendering morph cannot
 * leave stale copy on screen.
 *
 * State authority: the initial state is server-rendered from `cart.items`.
 * The component only re-reads the cart when something else changed it
 * (`cart:update` from another source) or when the page is restored from
 * bfcache. Re-reads are sequenced and never applied while an add/remove
 * is in flight, so a stale snapshot cannot overwrite a fresh mutation.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement[]} [variantButton] - Size buttons (multi-variant gift box only).
 * @property {HTMLButtonElement} addButton
 * @property {HTMLButtonElement} removeButton
 * @property {HTMLElement} status - Visually hidden live region.
 * @property {HTMLElement} errorMessage
 *
 * @extends {Component<Refs>}
 */
class HanroGiftBoxDrawer extends Component {
  /** @type {string | null} */
  #selectedVariantId = null;
  #busy = false;
  #syncSeq = 0;
  #syncPending = false;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #announceTimer;

  connectedCallback() {
    super.connectedCallback();
    this.#selectedVariantId = this.dataset.autoVariantId || null;
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    window.addEventListener('pageshow', this.#onPageShow);
    this.#morphObserver.observe(this, { attributes: true, attributeFilter: ['data-in-cart'] });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    window.removeEventListener('pageshow', this.#onPageShow);
    this.#morphObserver.disconnect();
    clearTimeout(this.#announceTimer);
  }

  /** @param {Event} event */
  #onCartUpdate = (event) => {
    const detail = /** @type {any} */ (event).detail;
    if (detail?.data?.source === SOURCE) return;
    // CartUpdateEvent carries the full cart; use it and skip the round trip.
    const resource = detail?.resource;
    if (!this.#busy && resource && Array.isArray(resource.items)) {
      this.#syncSeq++;
      this.#applyCart(resource);
      return;
    }
    this.#syncFromCart();
  };

  /**
   * Horizon's section-rendering morph (e.g. a combined-listing variant
   * change re-rendering <main>) syncs attributes in place from server HTML
   * that may predate a just-completed add/remove. Watch our own state
   * attribute: if anything other than this component changes it, re-read
   * the real cart so the stale server value cannot win.
   */
  #applyingState = false;
  #morphObserver = new MutationObserver(() => {
    if (this.#applyingState) return;
    this.#syncFromCart();
  });

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    // Only a bfcache restore can leave the server-rendered state stale.
    if (event.persisted) this.#syncFromCart();
  };

  /**
   * Re-reads the cart and applies the result unless a newer read or an
   * in-flight mutation has superseded it.
   */
  async #syncFromCart() {
    if (this.#busy) {
      this.#syncPending = true;
      return;
    }
    const seq = ++this.#syncSeq;
    try {
      const cart = await this.#fetchCart();
      if (seq !== this.#syncSeq || this.#busy) return;
      this.#applyCart(cart);
    } catch (error) {
      console.error('Gift box: could not read cart state', error);
    }
  }

  /** @param {any} cart */
  #applyCart(cart) {
    const productId = Number(this.dataset.productId);
    const line = cart.items.find((/** @type {any} */ item) => item.product_id === productId);
    this.#setInCart(line ? String(line.variant_id) : null, line ? line.key : null);
  }

  /** @returns {Promise<any>} */
  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`cart.js ${response.status}`);
    return response.json();
  }

  /**
   * @param {string | null} variantId
   * @param {string | null} [lineKey]
   */
  #setInCart(variantId, lineKey = null) {
    this.#applyingState = true;
    if (variantId) {
      this.dataset.inCart = 'true';
      this.dataset.cartVariantId = variantId;
      if (lineKey) this.dataset.cartLineKey = lineKey;
      else delete this.dataset.cartLineKey;
    } else {
      delete this.dataset.inCart;
      delete this.dataset.cartVariantId;
      delete this.dataset.cartLineKey;
    }
    // Mutation records are delivered asynchronously (microtask); clear the
    // flag after they have been observed.
    queueMicrotask(() => {
      this.#applyingState = false;
    });
  }

  /** @returns {string[]} */
  #cartSectionIds() {
    const ids = /** @type {string[]} */ ([]);
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.push(el.dataset.sectionId);
    });
    return ids;
  }

  /**
   * Returns the cart count currently shown in the header bubble, if any.
   * @returns {number | null}
   */
  #bubbleCount() {
    // Prefer the server-rendered bubble (correct across tabs); the
    // per-tab sessionStorage entry is only a fallback and only when fresh.
    const text = document.querySelector('cart-icon [ref="cartBubbleCount"]')?.textContent?.trim();
    const fromDom = Number(text);
    if (text && Number.isFinite(fromDom)) return fromDom;
    try {
      const stored = sessionStorage.getItem('cart-count');
      if (stored) {
        const parsed = JSON.parse(stored);
        const fresh = !parsed?.timestamp || Date.now() - Number(parsed.timestamp) < 10_000;
        const n = Number(parsed?.value ?? parsed);
        if (fresh && Number.isFinite(n)) return n;
      }
    } catch {
      // ignore
    }
    return null;
  }

  /**
   * Moves focus to the action button that is now visible, but only when
   * focus was inside this component (never steal it from elsewhere).
   */
  #refocus() {
    if (!this.contains(document.activeElement) && document.activeElement !== document.body) return;
    const target = this.dataset.inCart === 'true' ? this.refs.removeButton : this.refs.addButton;
    const fallback = /** @type {HTMLElement | null} */ (this.querySelector('summary'));
    (target && !target.disabled ? target : fallback)?.focus({ preventScroll: true });
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#busy = busy;
    // Any sync already in flight predates this mutation and is now stale.
    if (busy) this.#syncSeq++;
    this.classList.toggle('hanro-gift-box--busy', busy);
    this.refs.addButton?.setAttribute('aria-busy', String(busy));
    this.refs.removeButton?.setAttribute('aria-busy', String(busy));
    if (!busy && this.#syncPending) {
      this.#syncPending = false;
      this.#syncFromCart();
    }
  }

  /** @param {string} text */
  #announce(text) {
    const { status } = this.refs;
    if (!status) return;
    clearTimeout(this.#announceTimer);
    status.textContent = text;
    this.#announceTimer = setTimeout(() => {
      status.textContent = '';
    }, 4000);
  }

  #showError() {
    this.refs.errorMessage?.removeAttribute('hidden');
  }

  #hideError() {
    this.refs.errorMessage?.setAttribute('hidden', '');
  }

  /**
   * Handles size selection when the gift box has multiple variants.
   * @param {Event} event
   */
  selectVariant(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    const button = /** @type {HTMLButtonElement | null} */ (target.closest('.hanro-gift-box__variant'));
    const variantId = button?.dataset.variantId;
    if (!variantId) return;

    this.#selectedVariantId = variantId;

    const buttons = Array.isArray(this.refs.variantButton)
      ? this.refs.variantButton
      : this.refs.variantButton
        ? [this.refs.variantButton]
        : [];
    for (const btn of buttons) {
      btn.setAttribute('aria-pressed', String(btn.dataset.variantId === variantId));
    }

    if (this.refs.addButton) this.refs.addButton.disabled = false;
    this.#hideError();
  }

  /**
   * Adds the selected gift box variant to the cart.
   */
  async addGiftBox() {
    if (this.#busy || !this.#selectedVariantId) return;

    const variantId = this.#selectedVariantId;
    const marker = cartPerformance.createStartingMarker('gift-box:add');
    this.#hideError();
    this.#setBusy(true);

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    const sectionIds = this.#cartSectionIds();
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    /** @type {any} */
    let added;
    try {
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('javascript', { body: formData }));
      added = await response.json();
      if (!response.ok || added.status) {
        this.#showError();
        this.#setBusy(false);
        return;
      }
    } catch (error) {
      console.error('Gift box add to cart failed:', error);
      this.#showError();
      this.#setBusy(false);
      return;
    } finally {
      cartPerformance.measureFromMarker(marker);
    }

    // The add succeeded; everything from here is best-effort and must not
    // report a failure to the shopper.
    const previousCount = this.#bubbleCount();
    this.#setInCart(variantId, added.key || null);
    this.#refocus();
    this.#announce(this.refs.addButton?.dataset.addedText || Theme.translations.added || 'Added');

    // cart-icon treats a missing itemCount as 0, so always send a number:
    // the true count from /cart.js (one retry), else the visible count + 1.
    /** @type {number | null} */
    let itemCount = null;
    for (let attempt = 0; attempt < 2 && itemCount === null; attempt++) {
      try {
        const cart = await this.#fetchCart();
        itemCount = Number(cart.item_count);
      } catch {
        // retry once, then fall back
      }
    }
    if (itemCount === null || !Number.isFinite(itemCount)) {
      itemCount = (previousCount ?? 0) + 1;
    }

    this.dispatchEvent(
      new CartAddEvent({}, variantId, {
        source: SOURCE,
        itemCount,
        variantId,
        sections: added.sections,
      })
    );
    this.#setBusy(false);
  }

  /**
   * Removes the gift box from the cart.
   */
  async removeGiftBox() {
    if (this.#busy) return;

    const lineKey = this.dataset.cartLineKey;
    const variantId = this.dataset.cartVariantId;
    if (!lineKey && !variantId) {
      this.#syncFromCart();
      return;
    }

    const marker = cartPerformance.createStartingMarker('gift-box:remove');
    this.#hideError();
    this.#setBusy(true);

    /** @type {Record<string, unknown>} */
    const payload = { id: lineKey || variantId, quantity: 0, sections_url: window.location.pathname };
    const sectionIds = this.#cartSectionIds();
    if (sectionIds.length) payload.sections = sectionIds.join(',');

    let failed = false;
    try {
      const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body: JSON.stringify(payload) }));
      const cart = await response.json();

      if (!response.ok || cart.errors || cart.status) {
        failed = true;
        this.#showError();
        return;
      }

      this.#setInCart(null);
      this.#refocus();
      this.#announce(this.refs.removeButton?.dataset.removedText || 'Removed');

      // Horizon's cart drawer auto-opens on every `cart:update`, not only on
      // adds. A removal must not throw an overlay over the PDP, so suppress
      // auto-open for the synchronous dispatch only.
      const cartDrawer = document.querySelector('cart-drawer-component');
      const hadAutoOpen = cartDrawer?.hasAttribute('auto-open') ?? false;
      if (hadAutoOpen) cartDrawer.removeAttribute('auto-open');
      try {
        this.dispatchEvent(
          new CartUpdateEvent(cart, variantId || '', {
            source: SOURCE,
            itemCount: cart.item_count,
            variantId,
            sections: cart.sections,
          })
        );
      } finally {
        if (hadAutoOpen) cartDrawer.setAttribute('auto-open', '');
      }
    } catch (error) {
      failed = true;
      console.error('Gift box remove from cart failed:', error);
      this.#showError();
    } finally {
      cartPerformance.measureFromMarker(marker);
      this.#setBusy(false);
      // The line may no longer exist (checked out / emptied in another tab):
      // re-read the real cart so the drawer cannot get stuck in "added".
      if (failed) this.#syncFromCart();
    }
  }
}

if (!customElements.get('hanro-gift-box-drawer')) {
  customElements.define('hanro-gift-box-drawer', HanroGiftBoxDrawer);
}
